import { useEffect, useRef } from 'react'

// Ref-based fix for a render-loop bug class mobile hit in production
// (mobile-rn/src/components/PointEditSheet.tsx): passing a callback prop
// into an effect's dependency array means a parent re-render that produces
// a new callback reference re-fires the effect -- an infinite loop that
// silently starves rendering (no error, no warning, just nothing visibly
// updating). `onPreviewRef.current = onPreview` is a plain synchronous
// assignment in the render body, not inside its own effect -- wrapping it
// in an effect reintroduces a one-render staleness gap. This is React's
// own sanctioned "read the latest value inside another effect without
// adding it as a dependency" pattern, not RN-specific.
//
// Scope note (confirmed with mobile during review): only a callback that's
// invoked *from inside* an effect needs this treatment. onSave/onDelete/
// onCancel-style callbacks fired directly from button handlers never have
// this bug -- don't reflexively ref-wrap every callback prop.
export function useLivePreview(
  latText: string,
  lngText: string,
  onPreview: ((lat: number, lng: number) => void) | undefined,
): void {
  const onPreviewRef = useRef(onPreview)
  onPreviewRef.current = onPreview

  useEffect(() => {
    if (!onPreviewRef.current) return
    const lat = parseFloat(latText)
    const lng = parseFloat(lngText)
    if (Number.isNaN(lat) || Math.abs(lat) > 90) return
    if (Number.isNaN(lng) || Math.abs(lng) > 180) return
    onPreviewRef.current(lat, lng)
  }, [latText, lngText]) // deliberately not [onPreview]
}
